/**
 * Category-name helpers shared by the cake sections and the variant-B filter
 * chips.
 *
 * These live here, rather than beside either consumer, because the two MUST
 * agree exactly: a chip identifies its section by slug, so any drift between
 * how the chip and the section derive that slug produces a chip that matches
 * nothing and a category the visitor can never reach.
 *
 * Deliberately free of React and next-intl imports so both Server and Client
 * Components can use it.
 */

/**
 * Apostrophe variants to delete outright, so "Valentine's collection" and
 * "Valentine’s collection" both slug to `valentines-collection`.
 *
 * The original implementation's character class was `[''']` — three copies of
 * the SAME ASCII apostrophe (U+0027), so the curly U+2019 that word processors
 * and phone keyboards produce was never stripped. Since the category name is
 * typed into a Firestore admin field, that is the variant most likely to
 * actually occur.
 */
const APOSTROPHES = /['‘’ʼ]/g;

/**
 * Slugify a raw Firestore category name ("Gender Reveal", "Valentine's
 * collection") into the key used for `cakesGrid.categoryNames` lookups, the
 * `data-category` attribute, and filter identity.
 *
 * The output is restricted to `[a-z0-9-]`. That is not cosmetic: the slug is
 * written into a `data-category` attribute AND into a generated CSS attribute
 * selector, and if those two disagree by even one character the category's
 * filter chip silently matches nothing. Guaranteeing the character set here
 * makes the two agree by construction rather than by coincidence.
 *
 * Caveat: two category names differing only in stripped characters would
 * collapse to the same slug and filter as one. Acceptable — the alternative is
 * a slug that cannot be used in a selector at all.
 */
export function toCategorySlug(categoryName: string): string {
  return categoryName
    .toLowerCase()
    .replace(APOSTROPHES, "")
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-]/g, "")
    .replace(/-{2,}/g, "-")
    .replace(/^-+|-+$/g, "");
}

/**
 * Human-readable label for a category with no translation entry — Title Cases
 * the hyphenated slug. Keeps an unmapped Firestore category readable instead of
 * surfacing a raw slug to customers.
 */
export function formatCategoryFallback(categoryName: string): string {
  return categoryName
    .split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

/**
 * CSS-safe subset of a slug, for interpolation into the generated per-category
 * filter rules.
 *
 * Category names come from Firestore, so they are untrusted input on a path
 * that ends in `dangerouslySetInnerHTML`. Anything outside `[a-z0-9-]` is
 * dropped rather than escaped — a slug that needs escaping is not a slug we
 * want in a selector.
 */
export function toCssSafeSlug(slug: string): string {
  return slug.replace(/[^a-z0-9-]/g, "");
}
